import React, { useEffect, useMemo, useState } from 'react';
import { InputNumber, message, Spin } from 'antd';
import { Pickaxe, CalendarCheck, Target, Gift, Save, TriangleAlert } from 'lucide-react';
import { getGameConfig, setGameConfig } from '../../../../api/admin/gamification.api';
import './gamification.css';
import { AdminButton, AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';

// Nhãn cho từng khoá config, gom nhóm.
const GROUPS: { title: string; icon: React.ReactNode; items: { key: string; label: string; suffix?: string }[] }[] = [
  {
    title: 'Máy đào & điểm danh',
    icon: <Pickaxe size={18} className="text-[var(--adm-warning)]" />,
    items: [
      { key: 'miner.rate', label: 'Ca đào / giờ', suffix: 'Ca' },
      { key: 'miner.cap', label: 'Trần tích luỹ', suffix: 'Ca' },
      { key: 'miner.cycleHours', label: 'Chu kỳ đầy', suffix: 'giờ' },
      { key: 'checkin.reward', label: 'Thưởng điểm danh', suffix: 'Ca' },
      { key: 'checkin.streakMilestone', label: 'Mốc chuỗi thưởng', suffix: 'ngày' },
      { key: 'checkin.streakBonus', label: 'Thưởng đạt mốc chuỗi', suffix: 'Ca' },
    ],
  },
  {
    title: 'Nhiệm vụ',
    icon: <Target size={18} className="text-[var(--adm-primary)]" />,
    items: [
      { key: 'quest.like.target', label: 'Thích: số bài mục tiêu' },
      { key: 'quest.like.reward', label: 'Thích: thưởng', suffix: 'Ca' },
      { key: 'quest.share.target', label: 'Chia sẻ: số bài mục tiêu' },
      { key: 'quest.share.reward', label: 'Chia sẻ: thưởng', suffix: 'Ca' },
    ],
  },
  {
    title: 'Blind Box — chung',
    icon: <Gift size={18} className="text-[var(--adm-primary)]" />,
    items: [
      { key: 'box.days.min', label: 'Cosmetic ngắn hạn: tối thiểu', suffix: 'ngày' },
      { key: 'box.days.max', label: 'Cosmetic ngắn hạn: tối đa', suffix: 'ngày' },
      { key: 'box.days.mythic', label: 'Thần Thoại (từ box): số ngày', suffix: 'ngày' },
    ],
  },
  ...[
    ['basic', 'Basic Box'],
    ['rare', 'Rare Box'],
    ['epic', 'Epic Box'],
    ['vip', 'VIP Box'],
  ].map(([t, label]) => ({
    title: `Blind Box — ${label}`,
    icon: <Gift size={18} className="text-[var(--adm-primary)]" />,
    items: [
      { key: `box.${t}.price`, label: 'Giá mở', suffix: 'Ca' },
      { key: `box.${t}.ca`, label: 'Thưởng Ca (khi trúng giải Ca)', suffix: 'Ca' },
      { key: `box.${t}.w.miss`, label: 'Trọng số: Chúc may mắn' },
      { key: `box.${t}.w.ca`, label: 'Trọng số: Thưởng Ca' },
      { key: `box.${t}.w.cosmetic`, label: 'Trọng số: Cosmetic' },
      { key: `box.${t}.r.Common`, label: 'Độ hiếm: Cổ Điển' },
      { key: `box.${t}.r.Rare`, label: 'Độ hiếm: Hiếm' },
      { key: `box.${t}.r.Epic`, label: 'Độ hiếm: Sử Thi' },
      { key: `box.${t}.r.Legendary`, label: 'Độ hiếm: Huyền Thoại' },
      { key: `box.${t}.r.Mythic`, label: 'Độ hiếm: Thần Thoại' },
    ],
  })),
];

const ManagementGamification = () => {
  const [cfg, setCfg] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setCfg(await getGameConfig());
    } catch {
      message.error('Không tải được cấu hình.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const rateSum = useMemo(
    () =>
      ['Common', 'Rare', 'Epic', 'Legendary', 'Mythic'].reduce(
        (s, r) => s + (cfg[`box.rate.${r}`] || 0),
        0
      ),
    [cfg]
  );

  const save = async () => {
    setSaving(true);
    try {
      await setGameConfig(cfg);
      message.success('Đã lưu cấu hình. Áp dụng ngay.');
    } catch {
      message.error('Lưu thất bại.');
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Spin />
      </div>
    );

  const isBox = (t: string) => t.startsWith('Blind Box — ') && t !== 'Blind Box — chung';
  const plainGroups = GROUPS.filter((g) => !isBox(g.title));
  const boxGroups = GROUPS.filter((g) => isBox(g.title));
  const sensitive = (t: string) => t.startsWith('Máy đào');

  const field = (it: { key: string; label: string; suffix?: string }) => (
    <div className="flex items-center gap-1">
      <InputNumber
        min={0}
        className="w-full"
        value={cfg[it.key] ?? 0}
        onChange={(v) => setCfg((c) => ({ ...c, [it.key]: Number(v) || 0 }))}
      />
      {it.suffix && <span className="text-xs text-[var(--adm-muted)] w-8">{it.suffix}</span>}
    </div>
  );

  const groupExtra = (title: string) => (
    <>
      {sensitive(title) && (
        <AdminStatusPill tone="warning" icon={<TriangleAlert size={12} />}>
          Nhạy cảm
        </AdminStatusPill>
      )}
    </>
  );

  return (
    <div className="adm-page">
      <AdminPageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <CalendarCheck className="text-[var(--adm-warning)]" />
            Cấu hình Gamification (Ca đào)
          </span>
        }
        description="Chỉnh số liệu máy đào, nhiệm vụ và Blind Box. Lưu là áp dụng ngay (không cần deploy)."
        actions={
          <>
            <AdminButton variant="outline" onClick={load} disabled={saving}>
              Khôi phục
            </AdminButton>
            <AdminButton elevated icon={<Save size={18} />} loading={saving} onClick={save}>
              Lưu cấu hình
            </AdminButton>
          </>
        }
      />

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {plainGroups.map((g) => (
          <AdminCard
            key={g.title}
            title={
              <span className="inline-flex items-center gap-2">
                {g.icon} {g.title}
                {g.title.includes('tỉ lệ') && (
                  <span className={`text-xs ${rateSum > 0 ? 'ui-muted' : 'text-red-500'}`}>
                    (tổng trọng số: {rateSum})
                  </span>
                )}
              </span>
            }
            extra={<div className="flex items-center gap-2">{groupExtra(g.title)}</div>}
          >
            <div className="flex flex-col">
              {g.items.map((it) => (
                <div
                  key={it.key}
                  className="flex items-center justify-between gap-2 py-2 border-t border-[var(--adm-line)] first:border-t-0"
                >
                  <span className="text-sm text-[var(--adm-text)]">{it.label}</span>
                  <div className="w-40">{field(it)}</div>
                </div>
              ))}
            </div>
          </AdminCard>
        ))}
      </div>

      <AdminCard
        flush
        title={
          <span className="inline-flex items-center gap-2">
            <Gift size={18} className="text-[var(--adm-warning)]" /> Blind Box theo hộp
          </span>
        }
      >
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Thông số</th>
                {boxGroups.map((g) => (
                  <th key={g.title} className="adm-num">
                    {g.title.replace('Blind Box — ', '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {boxGroups[0].items.map((row, idx) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  {boxGroups.map((g) => (
                    <td key={g.items[idx].key} className="adm-num">
                      <div className="w-32 ml-auto">{field(g.items[idx])}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <div className="adm-save-bar">
        <AdminButton icon={<Save size={18} />} loading={saving} onClick={save}>
          Lưu cấu hình
        </AdminButton>
        <AdminButton variant="outline" onClick={load} disabled={saving}>
          Khôi phục
        </AdminButton>
      </div>
    </div>
  );
};

export default ManagementGamification;
